import React, { useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ResultDisplay from 'components/resultDisplay/ResultDisplay';
import emailSuccessIcon from 'assets/images/email_success.png';
import './EmailSent.scss';
import { Button } from 'antd';
import UpdateEmailModal from './subviews/UpdateEmailModal';
import { sendVerificationEmail, updateEmail } from 'services/emailService';
import { RegistrationRequestData } from 'types/dto/registration';

const EmailSent: React.FC  = () => {
  const { t } = useTranslation('email_sent');
  const location = useLocation();
  const [isUpdating, setIsUpdating] = useState(false);
  const [registrationData, setRegistrationData] = useState<RegistrationRequestData | undefined>(
    () => location.state?.registrationData
  );
  const [isUpdateEmailModalVisible, setIsUpdateEmailModalVisible] = useState(false);
  const [countdown, setCountdown] = useState(location.state?.fromConfirm ? 15 * 60 : 0);
  const email = registrationData?.email || '';

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [countdown]);

  const handleResendLink = useCallback(async () => {
    if (countdown > 0) return;

    try {
      const response: any = await sendVerificationEmail(email || '');
      if (response) {
        setCountdown(15 * 60);
      }
    } catch (error) {
      console.error('Resend failed:', error);
    }
  }, [countdown, email, t]);

  const handleUpdateEmailClick = () => {
    setIsUpdateEmailModalVisible(true);
  };

  // const handleUpdateEmailClose = () => {
  //   setIsUpdateEmailModalVisible(false);
  // };

  const handleUpdateEmailSuccess = async (newEmailString: string) => {
    if (!registrationData) {
      console.error('Registration data is missing. Cannot update email.');
      return;
    }

    const updatedRequestData: RegistrationRequestData = {
      ...registrationData,
      email: newEmailString,
    };

    try {
      setIsUpdating(true);

      await updateEmail(updatedRequestData);

      await sendVerificationEmail(newEmailString);
      setRegistrationData(updatedRequestData);

      setCountdown(15 * 60);

      // setUpdatedEmail(newEmailString);
      setIsUpdateEmailModalVisible(false);

    } catch (error) {
      console.error('Failed to update email:', error);
    }finally {
      setIsUpdating(false);
    }
  };

  const decodedEmail = email ? decodeURIComponent(email) : null;

  return decodedEmail ? (
    <div className='email-sent-container'>
      <ResultDisplay
        title={t('title')}
        imageUrl={emailSuccessIcon}
        onNext={handleResendLink}
        nextText={countdown > 0 ? t('resendIn', { time: formatTime(countdown) }) : t('resendButton')}
        showFooter={true}
        isNextDisabled={countdown > 0}
      >
        <div className="email-input-email-sent-message">
          <div>{t('message', { email: email })}</div>
        </div>
      </ResultDisplay>
      <Button
        type="link"
        className='update-email-button'
        onClick={handleUpdateEmailClick}
      >
        {t('updateEmailButton')}
      </Button>

      <UpdateEmailModal
        visible={isUpdateEmailModalVisible}
        isSubmitting={isUpdating}
        currentEmail={email}
        onClose={() => setIsUpdateEmailModalVisible(false)}
        onSuccess={handleUpdateEmailSuccess}
      />
    </div>
  ) : (
    <div className="vls-page-error">{t('emailNotFound')}</div>
  );
};

export default EmailSent;
